/**
 * POS 系統主頁面
 * 整合商品展示、購物車、確認訂單、付款流程
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import { z } from 'zod';
import { useOrdersCreate } from '../../api/posClient';
import type { OrdersCreate201 } from '@pos/api-sdk';
import { useCart } from '../../hooks/useCart';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useProducts, useTenant, useProduct } from '../../hooks/queries';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { ProductCard } from '../../components/ProductCard';
import {
  ProductVariantModal,
  findVariantByDimension,
  variantCombinationToLabel,
} from '../../components/ProductVariantModal';
import type { Product } from '../../components/ProductCard';
import { Cart } from '../../components/Cart';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  PaymentDialog,
  type PaymentMethod,
} from '../../components/PaymentDialog';
import { PinLogin } from './PinLogin';
import { normalizeImageUrl } from '../../utils/imageUrl';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { getDeviceToken } from '../../utils/deviceToken';
import { mutate } from 'swr';
import useSWR from 'swr';
import { fetcher } from '../../lib/fetcher';
import { usePersistentState } from '../../hooks/usePersistentState';
import { STORAGE_KEYS } from '../../utils/storage/storageKeyFactory';

/**
 * 訂單狀態
 */
type OrderStep = 'shopping' | 'confirming' | 'paying' | 'completed';

/**
 * 訂單完成頁面 Props
 */
interface OrderCompleteProps {
  orderNumber: string;
  onNewOrder: () => void;
}

/**
 * 訂單完成頁面
 */
function OrderComplete({ orderNumber, onNewOrder }: OrderCompleteProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8 text-center">
        <div className="mb-6">
          <svg
            className="w-16 h-16 text-green-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">訂單完成</h1>
          <p className="text-gray-600">感謝您的購買！</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600">訂單編號</p>
          <p className="text-lg font-mono font-bold text-gray-900">
            {orderNumber}
          </p>
        </div>

        <button
          onClick={onNewOrder}
          className="w-full bg-primary hover:bg-teal-700 text-primary-foreground font-medium py-3 px-4 rounded-lg transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 active:opacity-70 min-h-[44px]"
        >
          開始新訂單
        </button>
      </div>
    </div>
  );
}

/**
 * POS 主頁面
 */
export function POSPage() {
  const { state, clearCart, addItem } = useCart();
  const { handleError, handleSuccess } = useErrorHandler();
  const confirmDialogRef = useRef<any>(null);

  // 檢查是否有 Staff Session Token（PIN 碼登入）
  const [isStaffLoggedIn, setIsStaffLoggedIn] = useState(() => {
    return !!localStorage.getItem('staff_session_token');
  });

  // 取得 Authorization header（提供給 SDK fetch 選項使用）
  // 優先使用 Staff Session Token，如果沒有則使用 Tenant Token
  const getAuthFetchOptions = (): RequestInit | undefined => {
    const staffToken = localStorage.getItem('staff_session_token');
    const tenantToken = localStorage.getItem('tenant_token');
    const tenantId = localStorage.getItem('tenant_id');

    // 如果使用 Staff Session Token，需要特殊處理（可能需要不同的 header）
    const token = staffToken || tenantToken;

    if (!token || !tenantId) return undefined;
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
    };
  };

  // 處理 PIN 碼登入成功
  const handlePinLoginSuccess = (_sessionToken: string, _staffName: string) => {
    setIsStaffLoggedIn(true);
    // 可以選擇清除 tenant_token，只使用 staff_session_token
    // localStorage.removeItem('tenant_token');
  };

  // 所有 hooks 必須在條件返回之前調用（React Hooks 規則）
  const [currentStep, setCurrentStep] = useState<OrderStep>('shopping');
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [finalAmount, setFinalAmount] = useState(0); // 存儲折扣後的最終金額
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(
    undefined
  );
  const [pointsToRedeem, setPointsToRedeem] = useState(0); // 要折抵的點數
  // const [selectedCouponCodeId, setSelectedCouponCodeId] = useState<number | undefined>(undefined); // TODO: 優惠券功能已停用

  // 商品分類篩選狀態（持久化）
  const CategoryFilterSchema = z.string().default('all');
  const [categoryFilter, setCategoryFilter] = usePersistentState(
    STORAGE_KEYS.POS_CATEGORY_FILTER(),
    'all',
    CategoryFilterSchema
  );

  // 商品類型篩選狀態（持久化）
  const TypeFilterSchema = z
    .enum(['all', 'physical', 'service', 'voucher', 'subscription'])
    .default('all');
  const [typeFilter, setTypeFilter] = usePersistentState(
    STORAGE_KEYS.POS_TYPE_FILTER(),
    'all' as const,
    TypeFilterSchema
  );

  // 搜尋關鍵字（不持久化，使用 sessionStorage 或保持記憶體狀態）
  const [searchQuery, setSearchQuery] = useState('');

  // 頁面標題：依 URL channel 顯示「線上點餐」或「線上商店」
  const pageTitle = useMemo(() => {
    if (typeof window === 'undefined') return '線上商店';
    const params = new URLSearchParams(window.location.search);
    return params.get('channel') === 'online_order' ? '線上點餐' : '線上商店';
  }, []);

  // 規格選擇 Modal：有規格商品點擊後彈出
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [variantModalProduct, setVariantModalProduct] =
    useState<Product | null>(null);
  const [variantSelectedByDimension, setVariantSelectedByDimension] =
    useState<Record<string, string> | null>(null);
  const [variantQuantity, setVariantQuantity] = useState(1);

  // 檢查是否有 tenant_id（必須在所有條件返回之前檢查）
  const tenantId = useMemo(() => {
    return localStorage.getItem('tenant_id');
  }, []);

  // 如果沒有 tenant_id，重定向到 tenant-select（lobby）
  useEffect(() => {
    if (!tenantId) {
      // 沒有 tenant，重定向到租戶選擇頁面
      window.location.href = '/tenant-select';
    }
  }, [tenantId]);

  // 計算是否應該顯示 PIN 碼登入頁面（使用 useMemo 確保值穩定）
  // 一律以 staff 身份進入 POS：沒有 staff session 就顯示 PIN 登入（含 OWNER 也需輸入對應 staff 的 PIN）
  const shouldShowPinLogin = useMemo(() => {
    const hasTenantId = !!tenantId;
    return !isStaffLoggedIn && hasTenantId;
  }, [isStaffLoggedIn, tenantId]);

  const hasDeviceToken = useMemo(() => {
    return !!getDeviceToken(tenantId);
  }, [tenantId]); // 依賴 tenantId

  // 如果 Member 已登入但沒有 device token，自動註冊裝置
  useEffect(() => {
    const tenantToken = localStorage.getItem('tenant_token');
    const deviceToken = getDeviceToken(tenantId);

    if (tenantToken && tenantId && !deviceToken && !isStaffLoggedIn) {
      // Member 已登入但沒有 device token，自動註冊
      (async () => {
        const { autoRegisterDevice } = await import(
          '../../utils/deviceToken.js'
        );
        await autoRegisterDevice(tenantId, tenantToken);
      })();
    }
  }, [tenantId, isStaffLoggedIn]);

  // 查詢商家資訊（必須在所有條件返回之前調用）
  const authFetchOptions = useMemo(
    () => getAuthFetchOptions(),
    [isStaffLoggedIn]
  ); // 當登入狀態改變時重新計算
  // Staff session 時不應該調用 /api/tenants/me（需要 member 權限）
  // 注意：由於 Phase 1.1 已確保 token 互斥，這裡只需要檢查 staff_session_token 是否存在
  const isStaffSession = isStaffLoggedIn;
  // 判斷是否應該查詢商家資訊（只有 member session 才需要）
  const shouldEnableTenantQuery =
    !!authFetchOptions &&
    !shouldShowPinLogin &&
    (isStaffLoggedIn || !hasDeviceToken) &&
    !isStaffSession;
  // 判斷是否應該查詢商品（staff session 和 member session 都需要）
  const shouldEnableProductsQuery =
    !!authFetchOptions &&
    !shouldShowPinLogin &&
    (isStaffLoggedIn || !hasDeviceToken);

  // 查詢商家資訊（使用 custom hook）- 登出／PIN 畫面時不發請求，避免 401
  useTenant({
    fetch: authFetchOptions,
    swr: {
      enabled: shouldEnableTenantQuery,
      swrKey: shouldEnableTenantQuery ? undefined : null,
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    },
    revalidateOnFocus: false,
    dedupingInterval: 5000,
  });

  // 查詢商品列表（使用 custom hook）- 登出／PIN 畫面時不發請求，避免 401
  const { products: allProducts, isLoading: isLoadingProducts } = useProducts(
    {
      page: 1,
      limit: 50,
      sortBy: 'name',
      sortDir: 'asc',
      is_active: 1, // 只顯示啟用的商品
    },
    {
      fetch: authFetchOptions,
      swr: {
        enabled: shouldEnableProductsQuery,
        swrKey: shouldEnableProductsQuery ? undefined : null,
        revalidateOnFocus: true,
        dedupingInterval: 1000,
      },
    }
  );

  // 取得所有商品分類（含多分類：categories 與 category）
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    (allProducts || []).forEach((p: any) => {
      if (p.categories && Array.isArray(p.categories)) {
        p.categories.forEach((c: string) => categorySet.add(c));
      }
      if (p.category && typeof p.category === 'string') {
        categorySet.add(p.category);
      }
    });
    return Array.from(categorySet).sort((a, b) => a.localeCompare(b));
  }, [allProducts]);

  // 篩選商品（根據分類、類型和搜尋關鍵字；分類支援多分類）
  const products = useMemo(() => {
    let filtered = allProducts || [];

    // 分類篩選：商品屬於該分類（category 或 categories 任一符合）
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((p: any) => {
        if (p.categories && Array.isArray(p.categories)) {
          if (p.categories.includes(categoryFilter)) return true;
        }
        return p.category === categoryFilter;
      });
    }

    // 類型篩選
    if (typeFilter !== 'all') {
      filtered = filtered.filter((p: any) => p.type === typeFilter);
    }

    // 搜尋篩選
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((p: any) => {
        const name = typeof p.name === 'string' ? p.name.toLowerCase() : '';
        const description =
          typeof p.description === 'string' ? p.description.toLowerCase() : '';
        return name.includes(query) || description.includes(query);
      });
    }

    return filtered;
  }, [allProducts, categoryFilter, typeFilter, searchQuery]);

  // 格式化折扣標籤（簡短版本）
  // rate: 折扣百分比（例如：10 代表 10% off = 打九折）
  const formatDiscountLabel = (rate: number): string => {
    if (rate === 0) return '';
    if (rate >= 100) return '免費';
    const remainingPercent = 100 - rate; // 10% off = 90% = 打九折
    const chineseNumbers = [
      '',
      '一',
      '二',
      '三',
      '四',
      '五',
      '六',
      '七',
      '八',
      '九',
    ];
    const tens = Math.floor(remainingPercent / 10);
    const ones = remainingPercent % 10;

    if (ones === 0) {
      // 整十數：90 -> 九折, 80 -> 八折
      return `打${chineseNumbers[tens]}折`;
    } else if (tens === 0) {
      // 個位數：5 -> 打五折
      return `打${chineseNumbers[ones]}折`;
    } else {
      // 非整十數：95 -> 打九五折, 85 -> 打八五折
      return `打${chineseNumbers[tens]}${chineseNumbers[ones]}折`;
    }
  };

  // 批量獲取商品的折扣和點數資訊
  const productsWithDiscounts = useMemo(() => {
    if (!products || products.length === 0) return [];

    // 為每個商品創建一個 key，用於批量查詢折扣
    return products.map((product: any) => {
      const productId = String(product.id);
      const price =
        typeof product.unit_price_twd === 'number' ? product.unit_price_twd : 0;

      return {
        product,
        discountKey: `product-discount-${productId}-${price}`,
      };
    });
  }, [products]);

  // 批量查詢折扣資訊（使用單個 API 調用）
  const discountCalculationUrl =
    productsWithDiscounts.length > 0 && authFetchOptions
      ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'}/api/discounts/calculate-cart`
      : null;

  const { data: discountData } = useSWR<{
    data?: {
      item_discounts?: Array<{
        product_id: string;
        discount_amount: number;
        applied_rule_id: string | null;
      }>;
    };
  }>(
    discountCalculationUrl,
    () =>
      fetcher(discountCalculationUrl!, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authFetchOptions?.headers as HeadersInit),
        },
        body: JSON.stringify({
          items: productsWithDiscounts.map(({ product }) => ({
            product_id: String(product.id),
            quantity: 1,
            price:
              typeof product.unit_price_twd === 'number'
                ? product.unit_price_twd
                : 0,
          })),
        }),
      }),
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000, // 5 秒內不重複請求
    }
  );

  // 取得 Feature Flags
  const { isEnabled } = useFeatureFlags(tenantId || undefined);
  const isLoyaltyProgramEnabled = isEnabled('crm:loyalty_program');

  // 查詢點數規則列表（用於計算點數標籤）- 僅在功能啟用時查詢
  const pointsRulesUrl =
    authFetchOptions && isLoyaltyProgramEnabled
      ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'}/api/points-rules?page=1&limit=100&filter_is_active=1`
      : null;

  const { data: pointsRulesData } = useSWR<{
    data?: {
      rules?: Array<{
        id: string;
        points_per_yuan: number;
        target_type: 'ALL' | 'CATEGORY' | 'PRODUCT' | 'PRODUCTS';
        target_ids: string[];
        start_date: string;
        end_date: string;
      }>;
    };
  }>(pointsRulesUrl, url => fetcher(url, authFetchOptions), {
    revalidateOnFocus: true,
    dedupingInterval: 10000, // 10 秒內不重複請求
  });

  // 格式化點數標籤
  const formatPointsLabel = (
    pointsPerYuan: number,
    defaultPointsPerYuan: number = 20
  ): string => {
    if (pointsPerYuan <= 0) return '';
    if (pointsPerYuan === defaultPointsPerYuan) return ''; // 預設值不顯示
    if (pointsPerYuan > defaultPointsPerYuan) {
      const multiplier = pointsPerYuan / defaultPointsPerYuan;
      if (multiplier === 2) return '2倍點數';
      if (multiplier === 3) return '3倍點數';
      if (multiplier === 1.5) return '1.5倍點數';
      return `${multiplier.toFixed(1)}倍點數`;
    }
    return `${pointsPerYuan}點/元`;
  };

  // 獲取商品的點數規則
  const getPointsRuleForProduct = (
    product: any,
    pointsRules: any[]
  ): number | null => {
    const now = new Date();
    let bestRule: any = null;
    let bestPointsPerYuan = 0;

    for (const rule of pointsRules) {
      // 檢查規則是否在有效期內
      const startDate = new Date(rule.start_date);
      const endDate = new Date(rule.end_date);
      if (now < startDate || now > endDate) continue;

      // 檢查規則是否適用於此商品
      if (rule.target_type === 'ALL') {
        if (rule.points_per_yuan > bestPointsPerYuan) {
          bestRule = rule;
          bestPointsPerYuan = rule.points_per_yuan;
        }
      } else if (rule.target_type === 'CATEGORY') {
        const productCategory =
          typeof product.category === 'string' ? product.category : '';
        if (
          rule.target_ids.includes(productCategory) &&
          rule.points_per_yuan > bestPointsPerYuan
        ) {
          bestRule = rule;
          bestPointsPerYuan = rule.points_per_yuan;
        }
      } else if (
        rule.target_type === 'PRODUCT' ||
        rule.target_type === 'PRODUCTS'
      ) {
        const productId = String(product.id);
        if (
          rule.target_ids.includes(productId) &&
          rule.points_per_yuan > bestPointsPerYuan
        ) {
          bestRule = rule;
          bestPointsPerYuan = rule.points_per_yuan;
        }
      }
    }

    return bestRule ? bestPointsPerYuan : null;
  };

  // 建立商品折扣和點數映射
  const productDiscountMap = useMemo(() => {
    const map = new Map<
      string,
      { discountLabel: string; pointsLabel: string }
    >();
    const pointsRules = pointsRulesData?.data?.rules || [];
    const defaultPointsPerYuan = 20; // 預設 20點 = 1元

    // 處理折扣資訊
    if (discountData?.data?.item_discounts) {
      discountData.data.item_discounts.forEach(item => {
        const product = products.find(
          (p: any) => String(p.id) === item.product_id
        );
        if (product && item.discount_amount > 0) {
          const price =
            typeof product.unit_price_twd === 'number'
              ? product.unit_price_twd
              : 0;
          // 計算折扣百分比（例如：10% off = 打九折）
          const discountPercent =
            price > 0 ? Math.round((item.discount_amount / price) * 100) : 0;
          const discountLabel = formatDiscountLabel(discountPercent);

          // 獲取點數規則
          const pointsPerYuan = getPointsRuleForProduct(product, pointsRules);
          const pointsLabel = pointsPerYuan
            ? formatPointsLabel(pointsPerYuan, defaultPointsPerYuan)
            : '';

          map.set(item.product_id, { discountLabel, pointsLabel });
        }
      });
    }

    // 處理只有點數規則的商品（沒有折扣）
    products.forEach((product: any) => {
      const productId = String(product.id);
      if (!map.has(productId)) {
        const pointsPerYuan = getPointsRuleForProduct(product, pointsRules);
        const pointsLabel = pointsPerYuan
          ? formatPointsLabel(pointsPerYuan, defaultPointsPerYuan)
          : '';
        if (pointsLabel) {
          map.set(productId, { discountLabel: '', pointsLabel });
        }
      }
    });

    return map;
  }, [discountData, products, pointsRulesData, formatDiscountLabel]);

  // 記錄產品資料以便除錯
  console.log('[POS] 產品資料:', {
    productsCount: (allProducts || []).length,
    sampleProducts: (allProducts || []).slice(0, 3).map((p: any) => ({
      id: p.id,
      name: p.name,
      img_url: p.img_url,
      img_url_type: typeof p.img_url,
    })),
  });

  // 建立訂單（必須在所有條件返回之前調用）
  const createOrderMutation = useOrdersCreate({
    fetch: authFetchOptions,
  });

  // 規格 Modal：需在條件返回前調用，以符合 React Hooks 規則
  const variantModalProductIdForFetch =
    variantModalOpen &&
    variantModalProduct?.hasVariants &&
    !variantModalProduct?.variants?.length
      ? String(variantModalProduct.id)
      : undefined;
  const { product: variantDetailProduct } = useProduct(
    variantModalProductIdForFetch,
    {
      fetch: authFetchOptions,
      swr: { dedupingInterval: 0, revalidateOnFocus: false },
    }
  );
  useEffect(() => {
    if (
      !variantModalOpen ||
      !variantModalProduct?.hasVariants ||
      variantModalProduct.variants?.length
    )
      return;
    if (
      variantDetailProduct?.variants &&
      Array.isArray(variantDetailProduct.variants)
    ) {
      const variants = variantDetailProduct.variants.map(
        (v: {
          id?: string;
          combination?: Record<string, string>;
          unit_price_twd?: number;
        }) => ({
          id: v.id,
          combination: v.combination,
          unitPriceTwd:
            typeof v.unit_price_twd === 'number' ? v.unit_price_twd : 0,
        })
      );
      setVariantModalProduct(prev => (prev ? { ...prev, variants } : null));
    }
  }, [
    variantModalOpen,
    variantModalProduct?.id,
    variantModalProduct?.hasVariants,
    variantModalProduct?.variants?.length,
    variantDetailProduct,
  ]);
  const variantModalSelectedVariant = useMemo(() => {
    if (!variantModalProduct?.variants || !variantSelectedByDimension)
      return null;
    return findVariantByDimension(
      variantModalProduct.variants,
      variantSelectedByDimension
    );
  }, [variantModalProduct?.variants, variantSelectedByDimension]);

  // 如果沒有 tenant_id，不渲染任何內容（useEffect 會處理重定向）
  if (!tenantId) {
    return null;
  }

  // 未以 staff 登入則一律顯示 PIN 碼登入頁面（OWNER 也需輸入對應 staff 的 PIN）
  if (shouldShowPinLogin) {
    return <PinLogin onLoginSuccess={handlePinLoginSuccess} />;
  }

  // 以下代碼只有在登入後才執行
  // tenant 變數未使用，已移除

  /**
   * 開始結帳流程
   */
  const handleCheckout = () => {
    if (state.items.length === 0) return;
    setIsConfirmDialogOpen(true);
  };

  /** 商品卡片點擊：有規格則開規格 Modal，無規格則直接加入購物車 */
  const handleProductClick = (product: Product) => {
    if (product.hasVariants) {
      setVariantModalProduct(product);
      setVariantSelectedByDimension(null);
      setVariantQuantity(1);
      setVariantModalOpen(true);
    } else {
      const productId = String(product.id);
      addItem({
        id: productId,
        productId,
        name: product.name,
        price: product.price,
        thumbnail: product.thumbnail,
      });
    }
  };

  const handleVariantModalAddToCart = () => {
    if (!variantModalProduct || !variantModalSelectedVariant?.id) return;
    const productId = String(variantModalProduct.id);
    const variantId = variantModalSelectedVariant.id;
    const id = `${productId}:${variantId}`;
    const variantLabel = variantModalSelectedVariant.combination
      ? variantCombinationToLabel(variantModalSelectedVariant.combination)
      : undefined;
    const price =
      variantModalSelectedVariant.unitPriceTwd ?? variantModalProduct.price;
    for (let i = 0; i < variantQuantity; i++) {
      addItem({
        id,
        productId,
        productVariantId: variantId,
        variantLabel,
        name: variantModalProduct.name,
        price,
        thumbnail: variantModalProduct.thumbnail,
      });
    }
    setVariantModalOpen(false);
    setVariantModalProduct(null);
    setVariantSelectedByDimension(null);
    setVariantQuantity(1);
  };

  /**
   * 確認訂單
   */
  const handleConfirmOrder = (orderData: {
    items: Array<{
      id: number | string;
      name: string;
      price: number;
      quantity: number;
    }>;
    userId?: number | string;
    // couponCodeId?: number; // TODO: 優惠券功能已停用
    points_to_redeem?: number;
    totalAmount: number;
    finalAmount: number;
  }) => {
    // 存儲折扣後的最終金額、用戶ID、點數折抵
    setFinalAmount(orderData.finalAmount);
    setSelectedUserId(
      typeof orderData.userId === 'number' ? orderData.userId : undefined
    );
    setPointsToRedeem(orderData.points_to_redeem || 0);
    // setSelectedCouponCodeId(orderData.couponCodeId); // TODO: 優惠券功能已停用
    setIsConfirmDialogOpen(false);
    setCurrentStep('paying');
    setIsPaymentDialogOpen(true);
  };

  /**
   * 完成付款（由 PaymentDialog 呼叫，帶入所選付款方式）
   */
  const handlePaymentComplete = async (paymentData: {
    method: PaymentMethod;
    paidAmount: number;
    changeAmount?: number;
  }) => {
    const requestId = `order-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`📦 [建立訂單] 開始:`, {
      requestId,
      itemCount: state.items.length,
      totalAmount: state.totalAmount,
      finalAmount,
      userId: selectedUserId,
      pointsToRedeem,
      paymentMethod: paymentData.method,
      timestamp: new Date().toISOString(),
    });

    try {
      // 建立訂單
      // POS 來的訂單統一使用 '店消' 管道；付款方式由付款畫面帶入（現金／Line Pay）
      const orderData: any = {
        items: state.items.map(item => {
          const productId = item.productId ?? item.id;
          const payload: {
            product_id: string;
            quantity: number;
            product_variant_id?: string;
          } = {
            product_id: String(productId),
            quantity: item.quantity,
          };
          if (item.productVariantId) {
            payload.product_variant_id = item.productVariantId;
          }
          return payload;
        }),
        channel: '店消', // POS 訂單統一為店消
        payment_method: paymentData.method, // 店消也區分付款方式（cash / linepay）
      };

      // 只有在有會員時才添加 user_id
      if (selectedUserId) {
        orderData.user_id = selectedUserId;
        console.log(`👤 [建立訂單] 包含會員 ID: ${selectedUserId}`);
      } else {
        console.log(`👤 [建立訂單] 非會員訂單`);
      }

      // 添加點數折抵（如果有的話）
      if (pointsToRedeem > 0) {
        orderData.points_to_redeem = pointsToRedeem;
        console.log(`💰 [建立訂單] 點數折抵: ${pointsToRedeem} 元`);
      }

      // TODO: 優惠券功能已停用
      // 添加優惠券代碼 ID（如果有的話）
      // if (selectedCouponCodeId) {
      //   orderData.coupon_code_id = selectedCouponCodeId;
      // }

      console.log(`📤 [建立訂單] 發送訂單資料:`, {
        ...orderData,
        items: orderData.items.map((i: any) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
      });

      const orderResponse = await createOrderMutation.trigger(orderData);

      // SDK 不拋錯，4xx/5xx 時仍回傳 { status, data }，須在此判斷並走錯誤流程
      if (orderResponse.status !== 201) {
        const errorData = orderResponse.data as unknown as
          | Record<string, unknown>
          | undefined;
        let errorMessage = '建立訂單失敗';
        if (errorData?.error && typeof errorData.error === 'string') {
          errorMessage = errorData.error;
          if (errorData.details) {
            if (Array.isArray(errorData.details)) {
              const validationErrors = (
                errorData.details as { field?: string; message?: string }[]
              )
                .map(d => `${d.field ?? ''}: ${d.message ?? ''}`)
                .join(', ');
              errorMessage = validationErrors
                ? `驗證錯誤: ${validationErrors}`
                : errorMessage;
            } else if (
              typeof (errorData.details as { message?: string })?.message ===
              'string'
            ) {
              errorMessage = (errorData.details as { message: string }).message;
            }
          }
        } else if (
          errorData?.message &&
          typeof errorData.message === 'string'
        ) {
          errorMessage = errorData.message;
        }
        toast.error(errorMessage, { duration: 6000 });
        const syntheticError = Object.assign(new Error(errorMessage), {
          response: { data: errorData, status: orderResponse.status },
        });
        handleError(syntheticError, '建立訂單');
        throw syntheticError;
      }

      // 建立訂單成功後，更新快取
      mutate(key => typeof key === 'string' && key.startsWith('/api/orders'));

      console.log(`✅ [建立訂單] API 回應:`, {
        success:
          'success' in (orderResponse.data || {})
            ? (orderResponse.data as any).success
            : undefined,
        status: orderResponse.status,
        fullResponse: orderResponse.data,
      });

      // 設定訂單編號和重新獲取統計
      let newOrderNumber = `ORD-${Date.now()}`;
      if (orderResponse.data) {
        const orderData = orderResponse.data as OrdersCreate201;
        if (
          orderData.success &&
          orderData.data &&
          'order_number' in orderData.data
        ) {
          newOrderNumber = String(orderData.data.order_number);
        }
      }
      setOrderNumber(newOrderNumber);

      console.log(`🎉 [建立訂單] 訂單建立成功: ${newOrderNumber}`);

      // 清空購物車、用戶選擇和點數折抵
      clearCart();
      setSelectedUserId(undefined);
      setPointsToRedeem(0);
      // setSelectedCouponCodeId(undefined); // TODO: 優惠券功能已停用

      // 關閉付款對話框
      setIsPaymentDialogOpen(false);

      // 進入完成頁面
      setCurrentStep('completed');

      handleSuccess('訂單建立成功！');
    } catch (error: any) {
      console.error(`❌ [建立訂單] 錯誤:`, {
        requestId,
        error,
        errorType: error?.constructor?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        response: error?.response,
        responseData: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });

      // 解析詳細錯誤訊息
      let errorMessage = '建立訂單失敗';
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.error) {
          errorMessage = errorData.error;
          if (errorData.details) {
            if (Array.isArray(errorData.details)) {
              // Zod validation errors
              const validationErrors = errorData.details
                .map((d: any) => `${d.field}: ${d.message}`)
                .join(', ');
              errorMessage = `驗證錯誤: ${validationErrors}`;
            } else if (typeof errorData.details === 'string') {
              errorMessage += `: ${errorData.details}`;
            } else if (errorData.details.message) {
              errorMessage = errorData.details.message;
            }
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 6000 });
      handleError(error, '建立訂單');
    }
  };

  /**
   * 開始新訂單
   */
  const handleNewOrder = () => {
    setCurrentStep('shopping');
    setOrderNumber('');
    clearCart();
    setSelectedUserId(undefined);
    setPointsToRedeem(0);
    // setSelectedCouponCodeId(undefined); // TODO: 優惠券功能已停用
    // 重置確認對話框
    if (confirmDialogRef.current) {
      confirmDialogRef.current.resetForm();
    }
  };

  /**
   * 清空購物車並重置結帳狀態（快捷鍵 Cmd+R）
   * 僅重置 React 狀態與 localStorage，不整頁重新載入。
   */
  const handleClearCart = () => {
    clearCart();
    setSelectedUserId(undefined);
    setPointsToRedeem(0);
    setCurrentStep('shopping');
    setOrderNumber('');
    if (confirmDialogRef.current) {
      confirmDialogRef.current.resetForm();
    }
  };

  // 訂單完成頁面
  if (currentStep === 'completed') {
    return (
      <OrderComplete orderNumber={orderNumber} onNewOrder={handleNewOrder} />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* 頂部 Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-full mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* 中間：商品類型篩選 */}
            <div className="flex-1 flex items-center">
              <div className="flex space-x-2">
                <button
                  onClick={() => setTypeFilter('all')}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap',
                    typeFilter === 'all'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  全部
                </button>
                <button
                  onClick={() => setTypeFilter('physical')}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap',
                    typeFilter === 'physical'
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md shadow-green-500/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  實體商品
                </button>
                <button
                  onClick={() => setTypeFilter('service')}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap',
                    typeFilter === 'service'
                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md shadow-purple-500/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  課程
                </button>
                <button
                  onClick={() => setTypeFilter('voucher')}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap',
                    typeFilter === 'voucher'
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md shadow-orange-500/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  電子券
                </button>
                <button
                  onClick={() => setTypeFilter('subscription')}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap',
                    typeFilter === 'subscription'
                      ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-md shadow-pink-500/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  訂閱
                </button>
              </div>
            </div>

            {/* 搜尋列 - 移到右側 */}
            <div className="flex items-center gap-4">
              <div className="w-64">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search here"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ease-out text-sm"
                  />
                </div>
              </div>
            </div>

            {/* 右側：用戶資訊和操作 */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleClearCart}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-out active:opacity-70 shadow-sm hover:shadow-md"
                title="清空購物車 (Cmd+R)"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                清空
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 最上方：頁面標題與分類欄，可快速點選切換 */}
      <div className="max-w-full mx-auto px-6 pt-4 pb-2">
        <h1 className="text-xl font-bold text-gray-900 mb-3">{pageTitle}</h1>
        <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-thin">
          <button
            onClick={() => setCategoryFilter('all')}
            className={clsx(
              'px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap flex-shrink-0',
              categoryFilter === 'all'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/30'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            全部分類
          </button>
          {categories.map(category => {
            const isActive = categoryFilter === category;
            return (
              <button
                key={category}
                onClick={() => setCategoryFilter(category)}
                className={clsx(
                  'px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ease-out active:opacity-70 whitespace-nowrap flex-shrink-0',
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-full mx-auto px-6 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左側：商品列表 */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-6">
              {/* 商品網格 */}
              {isLoadingProducts ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[...Array(8)].map((_, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-2xl shadow-md overflow-hidden animate-pulse"
                    >
                      <div className="aspect-square bg-gray-200"></div>
                      <div className="p-4">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3 mb-3"></div>
                        <div className="h-8 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {products.length > 0 ? (
                    products.map((product: Record<string, unknown>) => {
                      const originalImgUrl =
                        typeof product.img_url === 'string'
                          ? product.img_url
                          : undefined;
                      const normalizedThumbnail =
                        normalizeImageUrl(originalImgUrl);
                      const productId = product.id;
                      const productKey =
                        typeof productId === 'number' ||
                        typeof productId === 'string'
                          ? String(productId)
                          : `product-${Math.random()}`;

                      // 獲取商品的折扣和點數標籤
                      const productDiscountInfo = productDiscountMap.get(
                        String(productId)
                      );

                      // API 規格：has_variants, variants (id, combination, unit_price_twd)
                      const hasVariants = Boolean(
                        (product as Record<string, unknown>).has_variants
                      );
                      const rawVariants = (product as Record<string, unknown>)
                        .variants as
                        | Array<{
                            id?: string;
                            combination?: Record<string, string>;
                            unit_price_twd?: number;
                          }>
                        | undefined;
                      const variants = Array.isArray(rawVariants)
                        ? rawVariants.map(v => ({
                            id: v.id,
                            combination: v.combination,
                            unitPriceTwd:
                              typeof v.unit_price_twd === 'number'
                                ? v.unit_price_twd
                                : 0,
                          }))
                        : undefined;

                      return (
                        <ProductCard
                          key={productKey}
                          product={{
                            id:
                              typeof productId === 'number' ||
                              typeof productId === 'string'
                                ? productId
                                : 0,
                            name:
                              typeof product.name === 'string'
                                ? product.name
                                : '',
                            price:
                              typeof product.unit_price_twd === 'number'
                                ? product.unit_price_twd
                                : 0,
                            listPrice:
                              typeof product.list_price_twd === 'number'
                                ? product.list_price_twd
                                : undefined,
                            thumbnail: normalizedThumbnail,
                            description:
                              typeof product.description === 'string'
                                ? product.description
                                : undefined,
                            category:
                              typeof product.category === 'string'
                                ? product.category
                                : undefined,
                            type:
                              typeof product.type === 'string'
                                ? (product.type as
                                    | 'physical'
                                    | 'service'
                                    | 'voucher'
                                    | 'subscription')
                                : 'physical',
                            discountLabel: productDiscountInfo?.discountLabel,
                            pointsLabel: productDiscountInfo?.pointsLabel,
                            hasVariants,
                            variants,
                          }}
                          onProductClick={handleProductClick}
                        />
                      );
                    })
                  ) : (
                    <div className="col-span-full text-center py-12">
                      <p className="text-muted-foreground">
                        目前沒有可用的商品
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        請確認資料庫中是否有啟用的商品
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 右側：訂單詳情區域 */}
          <div className="lg:col-span-1">
            <Cart onCheckout={handleCheckout} />
          </div>
        </div>
      </div>

      {/* 規格選擇 Modal */}
      <ProductVariantModal
        open={variantModalOpen}
        onOpenChange={setVariantModalOpen}
        product={variantModalProduct}
        selectedByDimension={variantSelectedByDimension}
        onSelectedByDimensionChange={setVariantSelectedByDimension}
        selectedVariant={variantModalSelectedVariant}
        quantity={variantQuantity}
        onQuantityMinus={() => setVariantQuantity(q => Math.max(1, q - 1))}
        onQuantityPlus={() => setVariantQuantity(q => Math.min(99, q + 1))}
        onAddToCart={handleVariantModalAddToCart}
        onClose={() => {
          setVariantModalOpen(false);
          setVariantModalProduct(null);
          setVariantSelectedByDimension(null);
          setVariantQuantity(1);
        }}
      />

      {/* 確認訂單對話框 */}
      <ConfirmDialog
        ref={confirmDialogRef}
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleConfirmOrder}
        products={allProducts?.map(p => ({
          id: p.id,
          type: p.type as
            | 'physical'
            | 'service'
            | 'voucher'
            | 'subscription'
            | undefined,
        }))}
      />

      {/* 付款對話框 */}
      <PaymentDialog
        isOpen={isPaymentDialogOpen}
        onClose={() => {
          setIsPaymentDialogOpen(false);
          setCurrentStep('confirming');
          setIsConfirmDialogOpen(true);
        }}
        onPaymentComplete={handlePaymentComplete}
        totalAmount={finalAmount || state.totalAmount}
      />
    </div>
  );
}
